import { memo, useState, useCallback, useEffect } from 'react';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import { styled, alpha } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';
import { NavSectionProps, NavListProps, NavConfigProps } from '../types';
import { navVerticalConfig } from '../config';
import NavItem from './nav-item';
import NavList from './nav-list';

// ----------------------------------------------------------------------
// STYLED COMPONENTS
// ----------------------------------------------------------------------

const StyledSubheader = styled('div')<{ config: NavConfigProps }>(({ theme, config }) => ({
  ...theme.typography.overline,
  fontSize: '0.75rem',
  fontWeight: 700,
  color: alpha(theme.palette.text.primary, 0.5),
  padding: theme.spacing(2, 1, 1, 1.5),
  margin: theme.spacing(1.5, 0, 0.5, 0),
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    color: theme.palette.text.primary,
  },
}));

const StyledCollapseIcon = styled('div')<{ open: boolean }>(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  transition: theme.transitions.create(['transform', 'background-color'], {
    duration: theme.transitions.duration.standard,
  }),
  transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
  '& svg': {
    fontSize: 16,
  },
}));

const StyledListWrapper = styled(List)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  '& .MuiCollapse-root': {
    transition: theme.transitions.create(['height'], {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
}));

const StyledNavSection = styled(Stack)(({ theme }) => ({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: theme.spacing(2),
    right: theme.spacing(2),
    height: 1,
    backgroundColor: alpha(theme.palette.divider, 0.5),
  },
  '&:first-of-type::before': {
    display: 'none',
  },
}));

// ----------------------------------------------------------------------
// NAV SECTION VERTICAL
// ----------------------------------------------------------------------

function NavSectionVertical({ data, config, sx, ...other }: NavSectionProps) {
  return (
    <Stack 
      sx={{ 
        py: 1.5,
        ...sx 
      }} 
      {...other}
    >
      {data.map((group, index) => (
        <StyledNavSection key={group.subheader || index}>
          <Group
            subheader={group.subheader}
            items={group.items}
            config={navVerticalConfig(config)}
          />
        </StyledNavSection>
      ))}
    </Stack>
  );
}

export default memo(NavSectionVertical);

type GroupProps = {
  subheader: string;
  items: NavListProps[];
  config: NavConfigProps;
};

function Group({ subheader, items, config }: GroupProps) {
  const [open, setOpen] = useState(true);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const renderContent = items.map((list) => (
    <NavList
      key={list.title + list.path}
      data={list}
      depth={1}
      hasChild={!!list.children}
      config={config}
    />
  ));

  return (
    <StyledListWrapper disablePadding>
      {subheader ? (
        <>
          <StyledSubheader onClick={handleToggle} config={config}>
            <span>{subheader}</span>
            <StyledCollapseIcon open={open}>
              <KeyboardArrowDownIcon />
            </StyledCollapseIcon>
          </StyledSubheader>

          <Collapse in={open} timeout="auto">
            {renderContent}
          </Collapse>
        </>
      ) : (
        renderContent
      )}
    </StyledListWrapper>
  );
}
